/**
 * ECGGenerator — genera muestras procedurales de ECG derivación II para
 * dibujar en un canvas tipo monitor hospitalario.
 *
 * La onda se construye como suma de gaussianas paramétricas (onda P,
 * complejo QRS, onda T) sobre un ciclo cardíaco. El generador expone un
 * método `sample(t)` que devuelve el voltaje en el momento t (ms), así
 * el caller avanza un reloj y dibuja cada muestra.
 *
 * Ritmos soportados en v1:
 *   - 'sinus': sinusal normal
 *   - 'tachy': taquicardia sinusal (HR alto, mismo patrón)
 *   - 'stemi': infarto con elevación ST (ST +2-3mm)
 *   - 'afib':  fibrilación auricular (sin P, RR irregular)
 *   - 'asistole': línea plana con artefacto mínimo
 *
 * Los valores están en "milivolts" simulados, rango típico -0.5 a +1.5 mV.
 * El canvas los mapea a pixeles según su altura.
 */

export type ECGRhythm = 'sinus' | 'tachy' | 'stemi' | 'afib' | 'asistole';

interface RhythmConfig {
  /** Amplitudes (mV) */
  pAmp: number;
  qAmp: number;
  rAmp: number;
  sAmp: number;
  tAmp: number;
  /** Elevación del segmento ST respecto a baseline (mV) */
  stElevation: number;
  /** Duraciones relativas dentro del ciclo (0..1) */
  pStart: number; pEnd: number;
  qStart: number; qEnd: number;
  rStart: number; rEnd: number;
  sStart: number; sEnd: number;
  stEnd: number;
  tStart: number; tEnd: number;
  /** Modulación aleatoria del intervalo R-R (0 = regular, 1 = muy irregular). */
  rrVariance: number;
  /** Si true, omite la onda P (ej. FA). */
  skipP: boolean;
}

const RHYTHMS: Record<ECGRhythm, RhythmConfig> = {
  sinus: {
    pAmp: 0.18, qAmp: -0.1, rAmp: 1.2, sAmp: -0.25, tAmp: 0.35,
    stElevation: 0,
    pStart: 0.04, pEnd: 0.11,
    qStart: 0.15, qEnd: 0.17,
    rStart: 0.17, rEnd: 0.22,
    sStart: 0.22, sEnd: 0.25,
    stEnd: 0.35,
    tStart: 0.35, tEnd: 0.48,
    rrVariance: 0.02, skipP: false,
  },
  tachy: {
    pAmp: 0.15, qAmp: -0.08, rAmp: 1.1, sAmp: -0.22, tAmp: 0.30,
    stElevation: 0,
    pStart: 0.04, pEnd: 0.10,
    qStart: 0.14, qEnd: 0.16,
    rStart: 0.16, rEnd: 0.21,
    sStart: 0.21, sEnd: 0.24,
    stEnd: 0.33,
    tStart: 0.33, tEnd: 0.46,
    rrVariance: 0.02, skipP: false,
  },
  stemi: {
    pAmp: 0.18, qAmp: -0.15, rAmp: 1.0, sAmp: -0.15, tAmp: 0.20,
    stElevation: 0.30, // elevación marcada del ST
    pStart: 0.04, pEnd: 0.11,
    qStart: 0.15, qEnd: 0.17,
    rStart: 0.17, rEnd: 0.22,
    sStart: 0.22, sEnd: 0.25,
    stEnd: 0.38,
    tStart: 0.38, tEnd: 0.52,
    rrVariance: 0.03, skipP: false,
  },
  afib: {
    pAmp: 0, qAmp: -0.1, rAmp: 1.15, sAmp: -0.25, tAmp: 0.32,
    stElevation: 0,
    pStart: 0, pEnd: 0,
    qStart: 0.15, qEnd: 0.17,
    rStart: 0.17, rEnd: 0.22,
    sStart: 0.22, sEnd: 0.25,
    stEnd: 0.35,
    tStart: 0.35, tEnd: 0.48,
    rrVariance: 0.22, skipP: true,
  },
  asistole: {
    pAmp: 0, qAmp: 0, rAmp: 0, sAmp: 0, tAmp: 0,
    stElevation: 0,
    pStart: 0, pEnd: 0,
    qStart: 0, qEnd: 0,
    rStart: 0, rEnd: 0,
    sStart: 0, sEnd: 0,
    stEnd: 0,
    tStart: 0, tEnd: 0,
    rrVariance: 0, skipP: true,
  },
};

export class ECGGenerator {
  private rhythm: ECGRhythm = 'sinus';
  private hr = 72; // BPM
  /** Fase actual dentro del ciclo (0..1). Avanza con el tiempo. */
  private phase = 0;
  /** Factor aleatorio del ciclo actual (se re-randomiza al empezar cada latido). */
  private cycleJitter = 1.0;

  setRhythm(rhythm: ECGRhythm): void {
    this.rhythm = rhythm;
  }

  setHeartRate(bpm: number): void {
    this.hr = Math.max(20, Math.min(240, bpm));
  }

  /**
   * Avanza el reloj interno `dt` segundos y devuelve la muestra actual
   * en milivolts. Llamar en cada frame del render.
   */
  sample(dt: number): number {
    const cfg = RHYTHMS[this.rhythm];
    // Duración del ciclo (segundos) según HR + variancia
    const baseCycle = 60 / this.hr;
    const cycleLen = baseCycle * this.cycleJitter;

    this.phase += dt / cycleLen;
    if (this.phase >= 1) {
      this.phase -= 1;
      // Nueva variancia para el próximo ciclo (FA tiene alta, sinusal casi cero)
      this.cycleJitter = 1 + (Math.random() - 0.5) * 2 * cfg.rrVariance;
    }

    if (this.rhythm === 'asistole') {
      // Línea plana con ruido blanco muy sutil
      return (Math.random() - 0.5) * 0.02;
    }

    const t = this.phase;
    let v = 0;
    // P wave
    if (!cfg.skipP && t >= cfg.pStart && t <= cfg.pEnd) {
      v += this.gauss(t, (cfg.pStart + cfg.pEnd) / 2, (cfg.pEnd - cfg.pStart) / 3) * cfg.pAmp;
    }
    // Q
    if (t >= cfg.qStart && t <= cfg.qEnd) {
      v += this.gauss(t, (cfg.qStart + cfg.qEnd) / 2, (cfg.qEnd - cfg.qStart) / 2.5) * cfg.qAmp;
    }
    // R (pico alto)
    if (t >= cfg.rStart && t <= cfg.rEnd) {
      v += this.gauss(t, (cfg.rStart + cfg.rEnd) / 2, (cfg.rEnd - cfg.rStart) / 2.5) * cfg.rAmp;
    }
    // S
    if (t >= cfg.sStart && t <= cfg.sEnd) {
      v += this.gauss(t, (cfg.sStart + cfg.sEnd) / 2, (cfg.sEnd - cfg.sStart) / 2.5) * cfg.sAmp;
    }
    // Segmento ST (elevación constante si aplica)
    if (t > cfg.sEnd && t < cfg.stEnd) {
      v += cfg.stElevation;
    }
    // T wave
    if (t >= cfg.tStart && t <= cfg.tEnd) {
      v += this.gauss(t, (cfg.tStart + cfg.tEnd) / 2, (cfg.tEnd - cfg.tStart) / 3) * cfg.tAmp;
    }
    // Ruido de baseline muy leve (artefacto respiratorio)
    v += (Math.random() - 0.5) * 0.015;
    return v;
  }

  /** Gaussiana normalizada: 1 en el centro, ~0 en ±3σ */
  private gauss(t: number, mu: number, sigma: number): number {
    const x = (t - mu) / sigma;
    return Math.exp(-x * x * 2);
  }
}

/**
 * Curva pletismográfica (SpO2) — pulsátil, sincrónica con la FC. Más
 * suave que el ECG: una sola onda con ascenso rápido y caída lenta.
 */
export class PlethGenerator {
  private hr = 72;
  private phase = 0;

  setHeartRate(bpm: number): void {
    this.hr = Math.max(20, Math.min(240, bpm));
  }

  sample(dt: number): number {
    const cycleLen = 60 / this.hr;
    this.phase += dt / cycleLen;
    if (this.phase >= 1) this.phase -= 1;
    // Onda asimétrica: ascenso rápido (0-0.2) + caída lenta (0.2-0.7) + isoeléctrica
    const t = this.phase;
    if (t < 0.2) {
      return Math.sin((t / 0.2) * Math.PI / 2); // ascenso
    }
    if (t < 0.6) {
      return Math.cos(((t - 0.2) / 0.4) * Math.PI / 2); // caída
    }
    return 0;
  }
}
